import * as React from "react";
import {
  Scheduler,
  MonthView,
  Appointments,
  Toolbar,
  DateNavigator,
  TodayButton,
  AppointmentTooltip,
  AppointmentForm,
} from "@devexpress/dx-react-scheduler-material-ui";
import { ViewState } from "@devexpress/dx-react-scheduler";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";

import TooltipAgenda from "./TooltipAgendaFolga";
import agendamentoFolgaApi from "../../api/agendamentoFolgaApi";
import DialogConfirmGeneric from "../../components/Generics/DialogConfirmGeneric";
import Container from "../../components/Generics/Container";
import GridCenter from "../../components/Generics/GridCenter";
import { useSnack } from "../../redux/snack/snackHooks";
import { useSessao } from "../../redux/sessao/sessaoHooks";
import { AgendamentoFolga } from "../../model/entities/AgendamentoFolga";

function folgaAtingeODia(dia: dayjs.Dayjs, start: Date, end: Date) {
  return (
    dia.isSame(start, "day") ||
    dia.isSame(end, "day") ||
    (dia.isAfter(start, "day") && dia.isBefore(end, "day"))
  );
}

const Appointment: React.FC<Appointments.AppointmentProps> = ({
  data,
  ...rest
}) => (
  <Appointments.Appointment
    {...rest}
    data={data}
    style={{
      borderRadius: 10,
      color: "#fff",
      background: "#4A90E2",
      padding: "6px 8px",
      fontSize: 13,
      fontWeight: 500,
      opacity: data?.diaOff ? 0.3 : 1,
    }}
  />
);

const Button: React.FC<TodayButton.ButtonProps> = (props) => (
  <TodayButton.Button
    {...props}
    getMessage={() => "Hoje"}
    style={{ backgroundColor: "#fff", borderRadius: 8 }}
  />
);

const CustomAppointmentTooltip: React.FC<AppointmentTooltip.ContentProps> = (
  props
) => {
  if (props.appointmentData?.diaOff) return null;
  return <TooltipAgenda id={props.appointmentData?.id} />;
};

const CustomCommandButton = () => null;

const CustomMonthLayout: React.FC<MonthView.LayoutProps> = (props) => (
  <MonthView.Layout {...props} style={{ height: 800 }} />
);

const TabelaDia: React.FC<{
  data: AgendamentoFolga[];
  diaSelecionado: dayjs.Dayjs;
}> = ({ data, diaSelecionado }) => {
  if (data.length === 0) {
    return (
      <div style={{ padding: 16, color: "#777" }}>Nenhuma folga neste dia.</div>
    );
  }

  return (
    <table style={{ width: "100%", marginTop: 12 }}>
      <thead>
        <tr style={{ background: "#f5f5f5" }}>
          <th style={{ padding: 8 }}>Período da Folga</th>
          <th style={{ padding: 8 }}>Motorista</th>
          <th style={{ padding: 8 }}>QNT. Dias Agendados</th>
        </tr>
      </thead>
      <tbody>
        {data.map((evt) => {
          const diasAgendados =
            dayjs(evt.endDate).diff(dayjs(evt.startDate), "day") + 1;

          return (
            <tr key={evt.id} style={{ borderBottom: "1px solid #eee" }}>
              <td style={{ padding: 8 }}>
                {dayjs(evt.startDate).format("DD/MM/YYYY")} {" - "}
                {dayjs(evt.endDate).format("DD/MM/YYYY")}
              </td>
              <td style={{ padding: 8 }}>
                {evt.solicitacaoPessoal?.nomeMotorista}
              </td>

              <td style={{ padding: 8 }}>{diasAgendados}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

type Props = {
  containerPage?: boolean;
  naoMostrarErro?: boolean;
};

const AgendaFolga: React.FC<Props> = ({ containerPage, naoMostrarErro }) => {
  const mostrarSnack = useSnack();
  const [sessao] = useSessao();

  const [currentDate, setCurrentDate] = useState(dayjs());
  const [diaSelecionado, setDiaSelecionado] = useState<dayjs.Dayjs | null>(
    null
  );

  const [agendamentos, setAgendamentos] = useState<AgendamentoFolga[]>([]);

  useEffect(() => {
    carregarAgendamentos();
  }, []);

  async function carregarAgendamentos() {
    try {
      const res = await agendamentoFolgaApi.getManyComplete();
      setAgendamentos(res.map(normalizarAllDay));
    } catch {
      if (!naoMostrarErro) mostrarSnack("Erro ao buscar agendamentos", "error");
    }
  }

  function normalizarAllDay(it: any) {
    const start = dayjs(it.startDate).startOf("day").toDate();
    const end = dayjs(it.endDate).endOf("day").toDate();

    return {
      ...it,
      startDate: start,
      endDate: end,
      allDay: true,
      title: it.titulo
        ? `${it.titulo} - ${it.solicitacaoPessoal?.nomeMotorista}`
        : "Folga",
    };
  }

  const eventosDoDia = useMemo(() => {
    if (!diaSelecionado) return [];
    return agendamentos.filter((a) =>
      folgaAtingeODia(diaSelecionado, a.startDate, a.endDate)
    );
  }, [diaSelecionado, agendamentos]);

  return (
    <GridCenter>
      <Container page={containerPage}>
        {diaSelecionado && (
          <>
            <button
              onClick={() => setDiaSelecionado(null)}
              style={{
                marginBottom: 8,
                padding: "6px 12px",
                borderRadius: 6,
                border: "1px solid #ccc",
                background: "#fff",
                cursor: "pointer",
              }}
            >
              ← Voltar para o mês
            </button>

            <TabelaDia data={eventosDoDia} diaSelecionado={diaSelecionado} />
          </>
        )}

        {/* ================= MODO MÊS ================= */}
        {!diaSelecionado && (
          <Scheduler data={agendamentos} locale="pt-BR">
            <ViewState
              currentDate={currentDate.toDate()}
              onCurrentDateChange={(d) => setCurrentDate(dayjs(d))}
            />

            <MonthView
              displayName="Mês"
              layoutComponent={CustomMonthLayout}
              timeTableCellComponent={(props) => (
                <MonthView.TimeTableCell
                  {...props}
                  onClick={() => setDiaSelecionado(dayjs(props.startDate))}
                />
              )}
            />

            <Appointments appointmentComponent={Appointment} />

            <AppointmentTooltip
              showCloseButton
              showDeleteButton={sessao.usuario?.perfil?.podeEditarAgendamento}
              contentComponent={CustomAppointmentTooltip}
            />

            <AppointmentForm
              basicLayoutComponent={() => null}
              commandLayoutComponent={CustomCommandButton}
            />

            <Toolbar />
            <DateNavigator />
            <TodayButton buttonComponent={Button} />
          </Scheduler>
        )}
      </Container>
    </GridCenter>
  );
};

export default AgendaFolga;
